import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  CartDeliveryDto,
  CartProductItemDto,
  CartShopDto,
  SetCartDeliveryRequest,
} from '@/services/platform/api/model';
import { extractErrorCode } from '@/lib/core/shop/cartErrors';
import {
  addCartShopProductV1,
  clearCartShopV1,
  getCartShopItemCountV1,
  getCartShopV1,
  removeCartShopGiftV1,
  removeCartShopProductV1,
  setCartShopDeliveryV1,
  setCartShopGiftV1,
  setCartShopProductQuantityV1,
} from '@/services/platform/api/me-—-shop-cart/me-—-shop-cart';

interface CartState {
  /**
   * The cart's server-side `Order.PublicGuid` — the `contextReference` a shop payment is created
   * with (`ShopPaymentContextHandler` refuses any reference that is not the caller's current cart).
   *
   * Deliberately **not** in `partialize`: a guid restored from localStorage may belong to a cart
   * that has already become an order, and paying against that is exactly what must never happen.
   * It is repopulated by `hydrate()` and by every cart-mutation response.
   */
  publicGuid?: string;
  items: CartProductItemDto[];
  itemCount: number;
  totalAmount: number;
  shipping: number;
  discount: number;
  currencyCode: string;
  giftMessage: string;
  delivery?: CartDeliveryDto;
  isHydrated: boolean;
  isCountHydrated: boolean;
  hydrate: () => Promise<void>;
  hydrateCount: () => Promise<void>;
  addItem: (variantId: string, quantity?: number) => Promise<{ success: boolean; error?: string }>;
  updateItem: (variantId: string, quantity: number) => Promise<void>;
  removeItem: (variantId: string) => Promise<void>;
  clear: () => Promise<void>;
  setGiftMessage: (message: string) => Promise<void>;
  setDelivery: (request: SetCartDeliveryRequest) => Promise<boolean>;
  setCartFromDto: (dto: CartShopDto) => void;
}

function applyCart(dto: CartShopDto | undefined) {
  return {
    publicGuid: dto?.publicGuid ?? undefined,
    items: dto?.items ?? [],
    itemCount: dto?.itemCount ?? 0,
    totalAmount: dto?.totalAmount ?? 0,
    shipping: dto?.shipping ?? 0,
    discount: dto?.discount ?? 0,
    currencyCode: dto?.currencyCode ?? 'EUR',
    giftMessage: dto?.gift?.message ?? '',
    delivery: dto?.delivery,
  };
}

/**
 * Client-side cart mirror. Persists a snapshot to localStorage so the header
 * badge and bag list are immediately available on reload, and syncs with the
 * `/me/cart/shop` API for the source of truth. Optimistic updates are avoided
 * here because product data (name/image/price) is resolved server-side from
 * the variant — actions await the response and replace state atomically.
 */
export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      publicGuid: undefined,
      items: [],
      itemCount: 0,
      totalAmount: 0,
      shipping: 0,
      discount: 0,
      currencyCode: 'EUR',
      giftMessage: '',
      delivery: undefined,
      isHydrated: false,
      isCountHydrated: false,

      hydrate: async () => {
        try {
          const response = await getCartShopV1();
          if (response.status === 200) {
            set({ ...applyCart(response.data), isHydrated: true });
            return;
          }
        } catch {
          // ignore — leave persisted snapshot in place
        }
        set({ isHydrated: true });
      },

      hydrateCount: async () => {
        try {
          const response = await getCartShopItemCountV1();
          if (response.status === 200) {
            set({ itemCount: response.data.count ?? 0, isCountHydrated: true });
            return;
          }
        } catch {
          // ignore — leave persisted snapshot in place
        }
        set({ isCountHydrated: true });
      },

      addItem: async (variantId, quantity = 1) => {
        const response = await addCartShopProductV1({ variantId, quantity });
        if (response.status === 201) {
          set(applyCart(response.data));
          return { success: true };
        }
        return { success: false, error: extractErrorCode(response.data) };
      },

      updateItem: async (variantId, quantity) => {
        const response = await setCartShopProductQuantityV1(variantId, { quantity });
        if (response.status === 200) {
          set(applyCart(response.data));
        }
      },

      removeItem: async variantId => {
        const previous = get().items;
        const previousItemCount = get().itemCount;
        const removed = previous.find(item => item.variantId === variantId);
        set({
          items: previous.filter(item => item.variantId !== variantId),
          itemCount: Math.max(0, previousItemCount - (removed?.quantity ?? 1)),
        });
        try {
          const response = await removeCartShopProductV1(variantId);
          if (response.status === 200) {
            set(applyCart(response.data));
          }
        } catch {
          set({ items: previous, itemCount: previousItemCount });
        }
      },

      clear: async () => {
        await clearCartShopV1();
        set({
          publicGuid: undefined,
          items: [],
          itemCount: 0,
          totalAmount: 0,
          giftMessage: '',
          delivery: undefined,
        });
      },

      setDelivery: async request => {
        try {
          const response = await setCartShopDeliveryV1(request);
          if (response.status === 200) {
            set(applyCart(response.data));
            return true;
          }
          return false;
        } catch {
          return false;
        }
      },

      setCartFromDto: dto => {
        set(applyCart(dto));
      },

      setGiftMessage: async message => {
        const trimmed = message.trim();
        set({ giftMessage: trimmed });
        try {
          const response = trimmed
            ? await setCartShopGiftV1({ message: trimmed })
            : await removeCartShopGiftV1();
          if (response.status === 200) {
            set(applyCart(response.data));
            return;
          }
          const refreshed = await getCartShopV1();
          if (refreshed.status === 200) {
            set(applyCart(refreshed.data));
          }
        } catch {
          // ignore — keep the locally-set message, next hydrate reconciles with the server
        }
      },
    }),
    {
      name: 'cart-storage',
      partialize: state => ({
        items: state.items,
        itemCount: state.itemCount,
        totalAmount: state.totalAmount,
        shipping: state.shipping,
        discount: state.discount,
        currencyCode: state.currencyCode,
        giftMessage: state.giftMessage,
      }),
    }
  )
);
